import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import PublicLayout from '../../components/PublicLayout';
import { plansAPI } from '../../lib/api';
import { MapPin, Info, ArrowRight, Users } from 'lucide-react';

const GLOBAL_NOTES = [
  "Abonamentele sunt nominale și netransmisibile.",
  "Valabilitatea abonamentului începe de la data cumpărării acestuia."
];

// Metadata statică per locație (adrese, note, ordine)
const LOCATION_META = {
  'Bazin Fiald (Hotel Fiald)': {
    id: 'fiald',
    meta: 'Strada Tazlăului 7A, Bacău',
    footerNotes: [...GLOBAL_NOTES, 'Accesul se face prin recepția hotelului Fiald.'],
    order: 0,
  },
  'Bazinul Olimpic Bacău': {
    id: 'olimpic',
    meta: 'Aleea Ghioceilor 10-14, Bacău',
    footerNotes: [...GLOBAL_NOTES, 'Programul poate varia în funcție de disponibilitatea bazinului (competiții sportive).'],
    order: 1,
  },
  'Bazinul EMD Academy': {
    id: 'emd',
    meta: 'Bulevardul Unirii 43, Bacău',
    footerNotes: [...GLOBAL_NOTES, 'Contact locație pentru detalii administrative: office@complexemd.ro'],
    order: 2,
  },
};

const CATEGORY_LABEL = {
  KIDS: 'Copii',
  ADULTS: 'Adulți',
  PERFORMANCE: 'Special & Performanță',
};

const CATEGORY_ORDER = { KIDS: 0, ADULTS: 1, PERFORMANCE: 2 };

const SubscriptionsPage = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    plansAPI.getAll()
      .then(res => {
        setPlans(res.data);
        // Setează prima locație activă
        const locs = [...new Set(res.data.map(p => p.location))].sort(
          (a, b) => (LOCATION_META[a]?.order ?? 99) - (LOCATION_META[b]?.order ?? 99)
        );
        if (locs.length > 0) setActiveId(LOCATION_META[locs[0]]?.id ?? locs[0]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Construiește structura de locații din planurile fetched
  const packs = useMemo(() => {
    const byLocation = {};
    plans.forEach(p => {
      if (!byLocation[p.location]) byLocation[p.location] = [];
      byLocation[p.location].push(p);
    });

    return Object.entries(byLocation)
      .sort(([a], [b]) => (LOCATION_META[a]?.order ?? 99) - (LOCATION_META[b]?.order ?? 99))
      .map(([location, locPlans]) => {
        const meta = LOCATION_META[location] ?? { id: location, meta: '', footerNotes: GLOBAL_NOTES, order: 99 };

        // Grupează pe categorii
        const byCategory = {};
        locPlans.forEach(p => {
          const cat = p.category || 'ADULTS';
          if (!byCategory[cat]) byCategory[cat] = [];
          byCategory[cat].push(p);
        });

        const sections = Object.entries(byCategory)
          .sort(([a], [b]) => (CATEGORY_ORDER[a] ?? 99) - (CATEGORY_ORDER[b] ?? 99))
          .map(([cat, catPlans]) => ({
            title: CATEGORY_LABEL[cat] ?? cat,
            offers: catPlans,
          }));

        return { ...meta, title: location, sections };
      });
  }, [plans]);

  const activePack = useMemo(() => packs.find(p => p.id === activeId), [packs, activeId]);

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#050505] border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
            <div className="max-w-2xl">
              <span className="text-[#6db025] text-xs font-heading uppercase tracking-[0.3em] mb-4 block">Tarife & Opțiuni</span>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase mb-6 leading-tight">Abonamente</h1>
              <p className="text-white/60 text-xl leading-relaxed">
                Alege locația și tipul de activitate potrivit pentru tine sau copilul tău.
                Pentru programare, stabilirea grupei și detalii suplimentare, te rugăm să ne contactezi.
              </p>
            </div>
            <Link to="/contact" className="bg-white/10 text-white px-8 py-4 font-heading font-bold uppercase tracking-wider flex items-center gap-2 hover:bg-white/20 transition-colors backdrop-blur-sm shrink-0">
              Contactează-ne <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-[#0A0A0A] min-h-screen">
        <div className="max-w-7xl mx-auto px-6">

          {/* Banner Reducere Frați */}
          <div className="mb-12 bg-[#6db025]/10 border border-[#6db025]/20 p-6 flex flex-col md:flex-row items-start md:items-center gap-5">
            <div className="bg-[#6db025] p-3 shrink-0"><Users className="text-black" size={24} /></div>
            <div>
              <h3 className="font-heading text-[#6db025] font-bold uppercase tracking-wider mb-2 text-lg">Reducere pentru Familii</h3>
              <p className="text-white/70 text-base leading-relaxed">
                Încurajăm sportul în familie! Oferim <strong className="text-white font-bold">10% reducere</strong> din prețul total al abonamentelor pentru 2 sau mai mulți copii înscriși (frate/soră). Se aplică pentru orice pachet de ședințe.
              </p>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-24 text-[#6db025] animate-pulse font-heading uppercase tracking-widest text-sm">
              Se încarcă abonamentele...
            </div>
          ) : packs.length === 0 ? (
            <div className="text-center py-24 text-white/30 font-heading uppercase tracking-widest text-sm">
              Nu există abonamente disponibile momentan.
            </div>
          ) : (
            <>
              {/* Tabs Locații */}
              <div className="flex flex-wrap gap-3 mb-12">
                {packs.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setActiveId(p.id)}
                    className={`px-6 py-4 font-heading uppercase tracking-wider text-sm font-bold transition-all border ${
                      p.id === activeId
                        ? 'bg-[#6db025] text-black border-[#6db025]'
                        : 'bg-[#121212] border-white/5 text-white/50 hover:text-white hover:border-white/20'
                    }`}
                  >
                    {p.title}
                  </button>
                ))}
              </div>

              {activePack && (
                <div className="animate-fade-in">
                  <div className="mb-12 flex items-center gap-3 text-white/40">
                    <MapPin size={18} className="text-[#6db025]" />
                    <span className="text-sm font-heading tracking-widest uppercase">{activePack.meta}</span>
                  </div>

                  <div className="space-y-16">
                    {activePack.sections.map((section) => (
                      <div key={section.title}>
                        <div className="flex items-center gap-3 mb-6">
                          <div className="w-2 h-2 bg-[#6db025]" />
                          <h2 className="font-heading text-2xl font-bold text-white uppercase">{section.title}</h2>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {section.offers.map((o) => (
                            <div
                              key={o.id}
                              className={`bg-[#121212] border p-6 flex flex-col transition-colors group ${
                                o.badge === 'Popular' ? 'border-[#6db025]/50 hover:border-[#6db025]' : 'border-white/5 hover:border-[#6db025]/30'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-4 mb-6">
                                <h3 className="text-white font-bold text-lg leading-tight group-hover:text-[#6db025] transition-colors">
                                  {o.activity}
                                </h3>
                                {o.badge && (
                                  <span className={`text-[10px] font-bold uppercase px-2.5 py-1 shrink-0 rounded-sm ${
                                    o.badge === 'Popular'
                                      ? 'bg-[#6db025] text-black'
                                      : 'bg-[#6db025]/10 text-[#6db025] border border-[#6db025]/20'
                                  }`}>
                                    {o.badge}
                                  </span>
                                )}
                              </div>

                              <div className="space-y-3 mb-8 flex-grow">
                                <div className="flex justify-between border-b border-white/5 pb-2 text-sm">
                                  <span className="text-white/40">Ședințe</span>
                                  <span className="text-white font-medium">{o.sessions ?? 'Nelimitat'}</span>
                                </div>
                                <div className="flex justify-between border-b border-white/5 pb-2 text-sm">
                                  <span className="text-white/40">Durată</span>
                                  <span className="text-white font-medium">50 min</span>
                                </div>
                                <div className="flex justify-between border-b border-white/5 pb-2 text-sm">
                                  <span className="text-white/40">Valabil</span>
                                  <span className="text-white font-medium">{o.validity_days} zile</span>
                                </div>
                              </div>

                              <div className="flex items-end justify-between mt-auto">
                                <div className="text-xs text-white/30 max-w-[50%] leading-tight">{o.note}</div>
                                <div className="text-4xl font-heading font-black text-white flex items-baseline gap-1">
                                  {o.price}
                                  <span className="text-sm font-bold text-[#6db025] uppercase tracking-widest">Lei</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {activePack.footerNotes?.length > 0 && (
                    <div className="mt-16 bg-[#121212] border border-[#6db025]/20 p-8 flex gap-4">
                      <Info className="text-[#6db025] shrink-0" size={24} />
                      <div>
                        <h4 className="font-heading text-white uppercase font-bold mb-4">Note Importante</h4>
                        <ul className="space-y-3">
                          {activePack.footerNotes.map((n, idx) => (
                            <li key={idx} className="text-white/65 text-base flex items-start gap-3 leading-relaxed">
                              <div className="w-1.5 h-1.5 rounded-full bg-[#6db025]/50 shrink-0 mt-1.5" />
                              <span>{n}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  <div className="mt-16 grid gap-6 md:grid-cols-3">
                    <div className="bg-[#050505] border border-white/5 p-6">
                      <div className="text-[#6db025] font-heading font-bold uppercase mb-2">1. Cum mă înscriu?</div>
                      <div className="text-white/55 text-base leading-relaxed">Contactează-ne telefonic sau pe WhatsApp și îți recomandăm oferta potrivită.</div>
                    </div>
                    <div className="bg-[#050505] border border-white/5 p-6">
                      <div className="text-[#6db025] font-heading font-bold uppercase mb-2">2. Programarea</div>
                      <div className="text-white/55 text-base leading-relaxed">Se stabilește grupa în funcție de vârstă, nivel de experiență și disponibilitate.</div>
                    </div>
                    <div className="bg-[#050505] border border-white/5 p-6">
                      <div className="text-[#6db025] font-heading font-bold uppercase mb-2">3. Echipament</div>
                      <div className="text-white/55 text-base leading-relaxed">Nu uita să aduci costum de baie, cască, ochelari, prosop și papuci de piscină.</div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </PublicLayout>
  );
};

export default SubscriptionsPage;
